<template>
  <ContentWrap>
    <el-alert type="info" :closable="false" show-icon class="mb-12px" title="工作日历：投影本所开庭 + 本所律师休假/外出。可登记请假。" />
    <el-form :inline="true" class="mb-12px">
      <el-form-item label="区间">
        <el-date-picker
          v-model="range"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始"
          end-placeholder="结束"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="load">查询</el-button>
        <el-button type="success" @click="openCreate">登记休假</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="eventType" label="类型" width="100" />
      <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
      <el-table-column prop="lawyerName" label="律师" width="100" />
      <el-table-column prop="firmCaseNo" label="案号" min-width="110" />
      <el-table-column prop="startTime" label="开始" width="170" />
      <el-table-column prop="endTime" label="结束" width="170" />
      <el-table-column prop="conflictHint" label="冲突" min-width="120" show-overflow-tooltip />
      <el-table-column label="操作" width="80">
        <template #default="{ row }">
          <el-button v-if="row.editable" link type="danger" @click="doDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <Dialog v-model="visible" title="登记休假/外出" width="480px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="律师" prop="lawyerId">
          <el-select v-model="form.lawyerId" filterable class="!w-100%">
            <el-option v-for="l in lawyers" :key="l.id" :label="`${l.name || '律师'}（#${l.id}）`" :value="l.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="类型" prop="eventType">
          <el-select v-model="form.eventType" class="!w-100%">
            <el-option label="休假" value="LEAVE" />
            <el-option label="外出" value="OUT" />
          </el-select>
        </el-form-item>
        <el-form-item label="标题" prop="title"><el-input v-model="form.title" /></el-form-item>
        <el-form-item label="开始" prop="startTime">
          <el-date-picker v-model="form.startTime" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" class="!w-100%" />
        </el-form-item>
        <el-form-item label="结束" prop="endTime">
          <el-date-picker v-model="form.endTime" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" class="!w-100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>
<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { FirmOfficeApi, type PracticeCalendarEventVO } from '@/api/firm/office'
import { FirmPortalApi, type FirmLawyerVO } from '@/api/firm/portal'
defineOptions({ name: 'FirmPracticeCalendar' })
const message = useMessage()
const loading = ref(false)
const list = ref<PracticeCalendarEventVO[]>([])
const range = ref<string[]>([])
const lawyers = ref<FirmLawyerVO[]>([])
const visible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({
  lawyerId: undefined as number | undefined,
  eventType: 'LEAVE',
  title: '',
  startTime: '',
  endTime: ''
})
const rules: FormRules = {
  lawyerId: [{ required: true, message: '必选', trigger: 'change' }],
  eventType: [{ required: true, message: '必选', trigger: 'change' }],
  title: [{ required: true, message: '必填', trigger: 'blur' }],
  startTime: [{ required: true, message: '必选', trigger: 'change' }],
  endTime: [{ required: true, message: '必选', trigger: 'change' }]
}
const defaultRange = () => {
  const now = new Date()
  const from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
  const fmt = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  }
  range.value = [fmt(from), fmt(to)]
}
const load = async () => {
  if (!range.value?.length || range.value.length < 2) return
  loading.value = true
  try {
    list.value = (await FirmOfficeApi.calendarEvents({ from: range.value[0], to: range.value[1] })) || []
  } finally {
    loading.value = false
  }
}
const openCreate = async () => {
  Object.assign(form, { lawyerId: undefined, eventType: 'LEAVE', title: '', startTime: '', endTime: '' })
  visible.value = true
  lawyers.value = (await FirmPortalApi.lawyerAssignableList()) || []
}
const submit = async () => {
  await formRef.value?.validate()
  if (!form.lawyerId) return
  saving.value = true
  try {
    await FirmOfficeApi.calendarCreate({
      lawyerId: form.lawyerId,
      eventType: form.eventType,
      title: form.title.trim(),
      startTime: form.startTime,
      endTime: form.endTime
    })
    message.success('已登记')
    visible.value = false
    await load()
  } finally {
    saving.value = false
  }
}
const doDelete = async (row: PracticeCalendarEventVO) => {
  if (!row.id) return
  try { await message.confirm('确认删除？') } catch { return }
  await FirmOfficeApi.calendarDelete(row.id)
  message.success('已删除')
  await load()
}
onMounted(async () => {
  defaultRange()
  await load()
})
</script>
